/*
 * Copyright (c) 2024 Anvilor Solutions LLC - All Rights Reserved.
 *
 * The information and source code contained herein is the exclusive property of
 * Anvilor Solutions LLC and may not be disclosed, examined, or reproduced in whole
 * or in part without the explicit written authorization from Anvilor Solutions LLC.
 */

import { AnvilorFormsAppStoreContext } from "../anvilor-forms-app-store.service";
import { QueryParamService } from "@anvilor/ngx-apps-core";
import { Observable } from "rxjs";
import { of } from "rxjs";
import { map } from "rxjs/operators";
import { PatchOperation } from "@anvilor/ngx-core-utils";
import { catchError } from "rxjs";
import { ParamMap } from "@angular/router";
import { lstrip } from "@anvilor/ngx-core-utils";

export const PATCHES_QPARAM = "patches";
export const PATCHES_ENCODING_QPARAM = "patches_enc";
export const PATCH_SINGLE_PREFIX = "patch_";

/**
 * Utility which parses/reads from the URL query parameters in order to determine
 * some link-specific "patches" to make on the data.
 *
 * The following is supported:
 *   A "patches" query parameter equal to a JSON array of patches to make (which can
 *   be encoded as base64 if the "patches_enc" query parameter is also provided as
 *   "base64".
 *
 *   Query parameters which start with "patch_", which are formatted as
 *   "patch_<field-path>=<value>" (The field path to set in the response_data, and
 *   the value to set it to). The field path is relative to /response/response_data,
 *   and it can omit the leading "/" for simplicity - though for explicit
 *   completeness it is recommended to use full field paths.
 */
export class LinkPatchesLoading {
  private qparams: QueryParamService;

  readonly responseDataPatches$: Observable<PatchOperation[] | null>;

  constructor(ctx: AnvilorFormsAppStoreContext) {
    this.qparams = ctx.qparams;

    this.responseDataPatches$ = this.qparams.queryParams$.pipe(
      map((qp) => this.readPatchesFromQueryParams(qp)),
      // Ensure bad patches strings don't blow up the loading...
      catchError((e) => {
        console.error(e);
        return of(null);
      })
    );
  }

  private readPatchesFromQueryParams(qp: ParamMap): PatchOperation[] {
    return this.readMainPatches(qp).concat(this.readIndividualPatches(qp));
  }

  private readMainPatches(qp: ParamMap): PatchOperation[] {
    if (qp.has(PATCHES_QPARAM)) {
      const decode =
        qp.get(PATCHES_ENCODING_QPARAM) === "base64" ? atob : (x: string) => x;
      const raw = decode(qp.get(PATCHES_QPARAM)!);
      return JSON.parse(raw) as PatchOperation[];
    }
    return [];
  }

  private readIndividualPatches(qp: ParamMap): PatchOperation[] {
    return qp.keys
      .filter((k) => k.startsWith(PATCH_SINGLE_PREFIX))
      .map((k) => ({
        op: "set",
        path: this.parseFieldFromPatchQueryParam(k),
        v: qp.get(k),
      }));
  }

  private parseFieldFromPatchQueryParam(k: string): string {
    return "/" + lstrip(k.substring(PATCH_SINGLE_PREFIX.length), "/");
  }
}
